import React, { memo } from "react";
import SearchResultListAdItem from "@components/molecules/SearchResultListAdItem";
import GemstoneSearchResultListItem from "@components/molecules/GemstoneSearchResultListItem";
import SearchResultListItem from "@components/molecules/SearchResultListItem";
import { Promotion } from "@src/types/diamondSearchResult";
import { Diamond } from "@src/types/DiamondDetail";
import { useTranslation } from "react-i18next";
import { css, useTheme } from "styled-components/native";

import styled from "styled-components/native";
import { ProductCategory } from "@utils/constants";
import SearchResultTabletListItem from "@components/molecules/SearchResultListItem/tabletListView";
import LineView from "@scenes/requests/utils/LineView";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import SearchResultListViewComponent from "@components/organisms/SearchResultListViewComponent";
import { ColumnStructure } from "./types";
import { Platform, View } from "react-native";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { MenuItemOptions } from "@utils/product/menuBuilders";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import { useEnableMobileWebView } from "@redux/slices/globalSlice";

type SearchResultComponentProps = {
  yItem: Diamond | Promotion;
  isCustomApp: boolean;
  selectedViewType: SearchResultsAndFavoritesEnum | string;
  menuOptionSelected: any;
  menuOptions: any;
  checkBoxSelectionUpdate: any;
  isSelected: boolean;
  handleItemClick: (
    item: Diamond,
    productType: string,
    productPrefix: string
  ) => void;
  favoriteFunctionClicked: (isFavorite: boolean, item: Diamond) => void;
  productType: ProductCategory;
  xOffset: any;
  isFilterVisible?: any;
  index?: number;
  showAvailabilityDot?: boolean;
  customizeColumnParams?: ColumnStructure[];
  userCurrency?: any;
  productPrefix?: any;
  sectionName?: string;
  handleLinkClick?: any;
};

const ResultListComponent = ({
  yItem,
  index,
  isCustomApp,
  selectedViewType,
  menuOptionSelected,
  menuOptions,
  checkBoxSelectionUpdate,
  isSelected,
  handleItemClick,
  favoriteFunctionClicked,
  productType,
  xOffset,
  isFilterVisible,
  showAvailabilityDot,
  customizeColumnParams,
  userCurrency,
  productPrefix,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  handleLinkClick,
}: SearchResultComponentProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const listMargin = theme.isMobile ? SpaceType.Basic : SpaceType.Double;
  const userInfo = useUserInfo();
  const enableMobileWebView = useEnableMobileWebView();
  const useMobileWebView =
    Platform.OS === "web" && theme.isMobile && enableMobileWebView;
  return (
    <ParentContainer
      listMargin={listMargin}
      enableMobileWebView={useMobileWebView}
    >
      {"promotion_index" in yItem ? (
        <SearchResultListAdItem
          key={yItem.media_url_url ?? yItem.promotion_index}
          item={yItem}
          xOffset={xOffset}
          isFilterVisible={isFilterVisible}
          enableMobileWebView={useMobileWebView}
        />
      ) : isCustomApp && theme.isMobile && !useMobileWebView ? (
        <SearchResultListViewComponent
          key={yItem.id}
          listExpanded={
            selectedViewType === SearchResultsAndFavoritesEnum.LIST_EXPANDED
          }
          mItem={yItem}
          menuOptions={MenuItemOptions(
            t,
            yItem.is_favorite ?? false,
            userInfo?.showBulkRequestFeature(
              productType,
              userInfo?.user?.price_mode
            ),
            yItem.in_cart ?? false,
            menuOptions
          )}
          menuOptionSelected={menuOptionSelected}
          checkBoxSelection={checkBoxSelectionUpdate}
          ismItemChecked={isSelected}
          handleItemClick={handleItemClick}
          favoriteClickedFunction={favoriteFunctionClicked}
          productType={productType}
          showAvailabilityDot={showAvailabilityDot}
          sectionName={sectionName}
          handleLinkClick={handleLinkClick}
        />
      ) : productType === ProductCategory.GEMSTONE &&
        theme.isMobile &&
        !useMobileWebView ? (
        <GemstoneSearchResultListItem
          key={yItem.id}
          mItem={yItem}
          listExpanded={
            selectedViewType === SearchResultsAndFavoritesEnum.LIST_EXPANDED
          }
          menuOptions={MenuItemOptions(
            t,
            yItem.is_favorite ?? false,
            userInfo?.showBulkRequestFeature(
              productType,
              userInfo?.user?.price_mode
            ),
            yItem.in_cart ?? false,
            menuOptions
          )}
          menuOptionSelected={menuOptionSelected}
          checkBoxSelection={checkBoxSelectionUpdate}
          ismItemChecked={isSelected}
          handleItemClick={handleItemClick}
          favoriteClickedFunction={favoriteFunctionClicked}
          productType={productType}
        />
      ) : theme.isMobile && !useMobileWebView ? (
        <SearchResultListItem
          key={yItem.id}
          listExpanded={
            selectedViewType === SearchResultsAndFavoritesEnum.LIST_EXPANDED
          }
          mItem={yItem}
          menuOptions={MenuItemOptions(
            t,
            yItem.is_favorite ?? false,
            userInfo?.showBulkRequestFeature(
              productType,
              userInfo?.user?.price_mode
            ),
            yItem.in_cart ?? false,
            menuOptions
          )}
          menuOptionSelected={menuOptionSelected}
          checkBoxSelection={checkBoxSelectionUpdate}
          ismItemChecked={isSelected}
          productType={productType}
          handleItemClick={handleItemClick}
          favoriteClickedFunction={favoriteFunctionClicked}
          sectionName={sectionName}
        />
      ) : (
        // Tablet: simple View, native ScrollView handles horizontal scroll
        <View key={yItem.id}>
          <SearchResultTabletListItem
            mItem={yItem}
            index={index}
            menuOptions={MenuItemOptions(
              t,
              yItem.is_favorite ?? false,
              userInfo?.showBulkRequestFeature(
                productType,
                userInfo?.user?.price_mode
              ),
              yItem.in_cart ?? false,
              menuOptions
            )}
            menuOptionSelected={menuOptionSelected}
            checkBoxSelection={checkBoxSelectionUpdate}
            ismItemChecked={isSelected}
            productType={productType}
            handleItemClick={handleItemClick}
            favoriteClickedFunction={favoriteFunctionClicked}
            fromBulkList={false}
            customizeColumnParams={customizeColumnParams}
            userCurrency={userCurrency}
            productPrefix={productPrefix}
            sectionName={sectionName}
            handleLinkClick={handleLinkClick}
          />
          <LineView background={theme["core-02"]} />
        </View>
      )}
    </ParentContainer>
  );
};

const ParentContainer = styled.View<{
  listMargin?: number;
  enableMobileWebView?: boolean;
}>`
  margin-top: ${({ theme, enableMobileWebView }) =>
    theme.isMobile && !enableMobileWebView ? 8 : 0}px;

  ${({ theme, listMargin, enableMobileWebView }) =>
    theme.isMobile &&
    !enableMobileWebView &&
    css`
      margin-start: ${listMargin}px;
      margin-end: ${listMargin}px;
    `}
`;

export default memo(ResultListComponent);
